import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { refundsService } from '../../services/api';

const AddRefundModal = ({ booking, onClose, onRefundAdded }) => {
  const [refundData, setRefundData] = useState({
    amount: 0,
    refundMethod: 'cash',
    bankingMethod: 'netbanking', // For bank refunds
    refundDate: new Date().toISOString().split('T')[0],
    reason: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);

  const refundMethods = [
    { id: 'cash', label: 'Cash' },
    { id: 'bank', label: 'Bank Transfer' },
  ];

  const bankingMethods = [
    { id: 'netbanking', label: 'Net Banking' },
    { id: 'credit_card', label: 'Credit Card' },
    { id: 'debit_card', label: 'Debit Card' },
    { id: 'upi', label: 'UPI' },
    { id: 'razorpay', label: 'Razorpay' },
  ];

  const handleInputChange = (field, value) => {
    setRefundData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!refundData.amount || refundData.amount <= 0) {
      toast.error('Please enter a valid refund amount');
      return;
    }

    if (!refundData.refundDate) {
      toast.error('Please select a refund date');
      return;
    }

    if (!refundData.reason.trim()) {
      toast.error('Please provide a reason for the refund');
      return;
    }

    try {
      setLoading(true);
      
      const refundPayload = {
        bookingId: booking.id,
        amount: parseFloat(refundData.amount),
        method: refundData.refundMethod,
        bankingMethod: refundData.refundMethod === 'bank' ? refundData.bankingMethod : null,
        refundDate: refundData.refundDate,
        reason: refundData.reason,
        description: refundData.description,
        status: 'completed' // Assuming refunds are processed immediately
      };

      console.log('Adding refund:', refundPayload);
      
      const response = await refundsService.create(refundPayload);
      
      if (response.success) {
        toast.success('Refund added successfully');
        if (onRefundAdded) {
          onRefundAdded(response.data);
        }
        onClose();
      } else {
        throw new Error(response.message || 'Failed to add refund');
      }
    } catch (error) {
      console.error('Error adding refund:', error);
      toast.error(error.message || 'Failed to add refund');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: '#E9DFCE',
        borderRadius: '12px',
        padding: '24px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto',
        margin: '20px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid #E3DFD3'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: '600',
            color: '#24332A'
          }}>
            Add Refund
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#5F6B61',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Booking Info */}
          <div style={{
            backgroundColor: '#F5F3EB',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#36433B' }}>
              Booking Details
            </h3>
            <p style={{ margin: '4px 0', color: '#5F6B61', fontSize: '14px' }}>
              <strong>Booking ID:</strong> {booking.id}
            </p>
            <p style={{ margin: '4px 0', color: '#5F6B61', fontSize: '14px' }}>
              <strong>Guest:</strong> {booking.guestName || booking.user?.name}
            </p>
            <p style={{ margin: '4px 0', color: '#5F6B61', fontSize: '14px' }}>
              <strong>Total Amount:</strong> ₹{booking.finalAmount || booking.totalPrice}
            </p>
          </div>

          {/* Refund Amount */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#36433B',
              marginBottom: '8px'
            }}>
              Refund Amount *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={refundData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '16px'
              }}
              placeholder="Enter refund amount"
              required
            />
          </div>

          {/* Refund Reason */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#36433B',
              marginBottom: '8px'
            }}>
              Reason for Refund *
            </label>
            <select
              value={refundData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '16px'
              }}
              required
            >
              <option value="">Select reason</option>
              <option value="cancellation">Booking Cancellation</option>
              <option value="overpayment">Overpayment</option>
              <option value="service_issue">Service Issue</option>
              <option value="duplicate_payment">Duplicate Payment</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Refund Method */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#36433B',
              marginBottom: '8px'
            }}>
              Refund Method *
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px'
            }}>
              {refundMethods.map(method => (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => handleInputChange('refundMethod', method.id)}
                  style={{
                    padding: '12px',
                    border: refundData.refundMethod === method.id ? '2px solid #8E3628' : '1px solid #D2CDBF',
                    borderRadius: '8px',
                    backgroundColor: refundData.refundMethod === method.id ? '#F9EEEB' : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px'
                  }}
                >
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          {/* Banking Method (if bank is selected) */}
          {refundData.refundMethod === 'bank' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#36433B',
                marginBottom: '8px'
              }}>
                Banking Method
              </label>
              <select
                value={refundData.bankingMethod}
                onChange={(e) => handleInputChange('bankingMethod', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              >
                {bankingMethods.map(method => (
                  <option key={method.id} value={method.id}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Refund Date */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#36433B',
              marginBottom: '8px'
            }}>
              Refund Date *
            </label>
            <input
              type="date"
              value={refundData.refundDate}
              onChange={(e) => handleInputChange('refundDate', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '16px'
              }}
              required
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#36433B',
              marginBottom: '8px'
            }}>
              Additional Notes
            </label>
            <textarea
              value={refundData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '16px',
                resize: 'vertical'
              }}
              placeholder="Additional notes about this refund..."
            />
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '12px 24px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: '#36433B',
                fontSize: '16px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: loading ? '#A5A89B' : '#8E3628',
                color: 'white',
                fontSize: '16px',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Processing...
                </>
              ) : (
                <>
                  Add Refund
                </>
              )}
            </button>
          </div>
        </form>

        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default AddRefundModal;
